import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useRef, useState } from 'react';
import { type PublicContractSignatures, publicProposalApi } from '@/api/commercial/public-proposal';
import { toast } from '@/hooks/use-toast';
import { type ContractViewData, shouldPollContractSignatures } from './contract-state';
import type { ContractStepProps } from './contract-step';

interface PublicProposalContractStep {
  canAdvance: boolean;
  isWaitingSignature: boolean;
  stepProps: ContractStepProps;
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError<{ message?: string; error?: string }>(error)) {
    return (
      error.response?.data.message ??
      error.response?.data.error ??
      'Não foi possível enviar o contrato para assinatura.'
    );
  }
  return error instanceof Error
    ? error.message
    : 'Não foi possível enviar o contrato para assinatura.';
}

export function usePublicProposalContractStep(token: string): PublicProposalContractStep {
  const queryClient = useQueryClient();
  // React state only changes after the event handler returns. Keep a synchronous
  // lock as well so two clicks in the same render cannot start concurrent POSTs.
  const inFlightVersionIds = useRef(new Set<string>());
  const [pendingVersionIds, setPendingVersionIds] = useState<Set<string>>(() => new Set());
  const [resendingVersionIds, setResendingVersionIds] = useState<Set<string>>(() => new Set());
  const [errorsByVersionId, setErrorsByVersionId] = useState<Record<string, string>>({});
  const markPending = (versionId: string) => {
    setPendingVersionIds((current) => new Set(current).add(versionId));
    setErrorsByVersionId((current) => {
      const { [versionId]: _removed, ...remaining } = current;
      return remaining;
    });
  };
  const markSettled = (versionId: string) => {
    setPendingVersionIds((current) => {
      const next = new Set(current);
      next.delete(versionId);
      return next;
    });
  };
  const markError = (versionId: string, error: unknown) => {
    setErrorsByVersionId((current) => ({ ...current, [versionId]: errorMessage(error) }));
  };
  const queryKey = ['publicProposalContractSignatures', token] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => publicProposalApi.findContractSignatures(token),
    enabled: Boolean(token),
    retry: false,
    staleTime: 5_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: (state) =>
      shouldPollContractSignatures(
        state.state.data?.contracts.map((item) => item.status) ?? [],
        pendingVersionIds.size,
      )
        ? 7_000
        : false,
  });
  const send = useMutation({
    retry: false,
    mutationFn: (versionId: string) => publicProposalApi.sendContractSignature(token, versionId),
    onMutate: markPending,
    onError: (error, versionId) => {
      markError(versionId, error);
      toast({
        title: 'Erro ao enviar contrato',
        description: errorMessage(error),
        variant: 'destructive',
      });
    },
    onSettled: (_data, _error, versionId) => {
      markSettled(versionId);
      return queryClient.invalidateQueries({ queryKey });
    },
    onSuccess: (data) => {
      queryClient.setQueryData<PublicContractSignatures>(queryKey, data);
      if (data.contracts.some((contract) => contract.status === 'waiting')) {
        toast({
          variant: 'success',
          title: 'Contrato enviado com sucesso',
          description: 'A Clicksign recebeu a solicitação de assinatura.',
        });
      }
    },
  });
  const resend = useMutation({
    retry: false,
    mutationFn: (versionId: string) => publicProposalApi.resendContractSignature(token, versionId),
    onMutate: (versionId) => {
      setResendingVersionIds((current) => new Set(current).add(versionId));
    },
    onError: (error, _versionId) => {
      toast({
        title: 'Erro ao reenviar contrato',
        description: errorMessage(error),
        variant: 'destructive',
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData<PublicContractSignatures>(queryKey, data);
      return queryClient.invalidateQueries({ queryKey });
    },
    onSettled: (_data, _error, versionId) => {
      setResendingVersionIds((current) => {
        const next = new Set(current);
        next.delete(versionId);
        return next;
      });
    },
  });
  const sendOnce = useCallback(
    (versionId: string) => {
      if (inFlightVersionIds.current.has(versionId)) return;
      inFlightVersionIds.current.add(versionId);
      send.mutate(versionId, {
        onSettled: () => {
          inFlightVersionIds.current.delete(versionId);
        },
      });
    },
    [send],
  );
  const contracts: ContractViewData[] = (query.data?.contracts ?? []).map((item) => ({
    contractVersionId: item.contractVersionId,
    type: item.type,
    version: item.version,
    status: item.status,
    signerName: item.signerName ?? '',
    signerEmail: item.signerEmailMasked ?? '',
    sentAt: item.sentAt,
    signedAt: item.signedAt,
  }));
  return {
    canAdvance: Boolean(query.data?.allSigned),
    isWaitingSignature: contracts.some(
      (contract) => contract.status === 'waiting' || contract.status === 'sending',
    ),
    stepProps: {
      contracts,
      isLoading: query.isLoading,
      pendingVersionIds,
      resendingVersionIds,
      errorsByVersionId,
      loadErrorMessage: query.error?.message ?? null,
      onSend: sendOnce,
      onRetry: sendOnce,
      onResend: (versionId) => resend.mutate(versionId),
      onLoadRetry: () => query.refetch(),
    },
  };
}
